import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { SOCKET_EVENTS } from '../constants/socketEvents';
import api from '../api/api';
import { DisplayContext, formatOrderId } from '../utils/orderFormatter';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Card,
  CardContent,
  Typography,
  Grid,
  Box
} from '@mui/material';
import DateFilterDropdown from '../components/DateFilterDropdown';
import { AppIconButton } from '../components/ui';
import {
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  AttachMoney as AttachMoneyIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { CURRENCY } from '../config/currency';

interface Order {
  id: number;
  orderNumber: string;
  status: string;
  total: number;
  createdAt: string;
  user: {
    id: number;
    username: string;
    email: string;
  };
  vendor?: {
    id: number;
    username: string;
  };
  items: Array<{
    quantity: number;
    price: number;
    menuItem: {
      name: string;
      price: number;
    };
  }>;
}

interface Stats {
  totalOrders: number;
  totalRevenue: number;
  activeOrders: number;
  totalVendors: number;
}

const AdminOrders: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const socket = useSocket();
  const connected = socket?.isConnected || false;

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats>({
    totalOrders: 0,
    totalRevenue: 0,
    activeOrders: 0,
    totalVendors: 0
  });
  const [subscribedVendors, setSubscribedVendors] = useState<Set<number>>(new Set());

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [dateFilter, setDateFilter] = useState('recent');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  const calculateOrderTotal = useCallback((order: Order) => {
    return order.items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch all orders from the simple admin endpoint
      const response = await api.get('/admin/orders');
      let allOrders = response.data || [];

      // Client-side filtering
      if (statusFilter !== 'ALL') {
        allOrders = allOrders.filter((order: Order) => order.status === statusFilter);
      }

      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        allOrders = allOrders.filter((order: Order) =>
          order.orderNumber?.toLowerCase().includes(search) ||
          order.user?.username?.toLowerCase().includes(search) ||
          order.user?.email?.toLowerCase().includes(search)
        );
      }

      if (dateFilter !== 'all') {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const twoDaysAgo = new Date(today);
        twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

        allOrders = allOrders.filter((order: Order) => {
          const orderDate = new Date(order.createdAt);
          switch (dateFilter) {
            case 'today':
              return orderDate >= today;
            case 'yesterday':
              return orderDate >= yesterday && orderDate < today;
            case 'recent':
              return orderDate >= yesterday; // Today and yesterday
            default:
              return true;
          }
        });
      }

      // Sort by date descending
      allOrders.sort((a: Order, b: Order) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      // Client-side pagination
      const startIndex = (page - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedOrders = allOrders.slice(startIndex, endIndex);

      setOrders(paginatedOrders);
      setTotalPages(Math.ceil(allOrders.length / itemsPerPage));

      // Update stats based on all orders
      const activeStatuses = ['PENDING', 'ACCEPTED', 'PREPARING', 'READY'];
      const activeOrders = allOrders.filter((order: Order) =>
        activeStatuses.includes(order.status)
      ).length;

      const totalRevenue = allOrders.reduce((sum: number, order: Order) =>
        sum + calculateOrderTotal(order), 0
      );

      setStats({
        totalOrders: allOrders.length,
        totalRevenue,
        activeOrders,
        totalVendors: new Set(allOrders.map((o: Order) => o.vendor?.id).filter(Boolean)).size
      });
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to fetch orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateFilter, searchTerm, page, calculateOrderTotal]);

  // Handle Socket.IO notifications for real-time order updates
  const handleSocketNotification = useCallback((notification: any) => {
    console.log('Admin Orders received Socket.IO notification:', notification);

    const { event, data } = notification || {};

    // Handle order-related notifications using standardized event names
    if (event === SOCKET_EVENTS.NEW || event === SOCKET_EVENTS.UPDATED || event === SOCKET_EVENTS.STATUS_UPDATE) {
      // Refresh orders when updates are received
      fetchOrders();

      // Show notification for new orders
      if (event === SOCKET_EVENTS.NEW) {
        const orderInfo = data || {};
        // Prefer customOrderId (readable format) for display
        const displayOrderId = orderInfo.customOrderId || orderInfo.orderNumber || orderInfo.orderId;
        toast.success(`New order ${formatOrderId(displayOrderId, DisplayContext.VENDOR)} received!`, {
          duration: 4000,
          icon: '📋'
        });
      }
    }
  }, [fetchOrders]);

  // Setup Socket.IO subscriptions for all vendors
  useEffect(() => {
    if (socket && orders.length > 0) {
      // Get unique vendor IDs from orders
      const vendorIds = new Set<number>();
      orders.forEach(order => {
        if (order.vendor?.id) {
          vendorIds.add(order.vendor.id);
        }
      });

      // Subscribe to vendors we haven't subscribed to yet
      vendorIds.forEach(vendorId => {
        if (!subscribedVendors.has(vendorId)) {
          console.log(`Admin subscribing to vendor ${vendorId} notifications via Socket.IO`);
          socket.subscribeToVendor(vendorId);
          setSubscribedVendors(prev => new Set(prev).add(vendorId));
        }
      });

      // Listen for order events using standardized constants
      const handleNewOrder = (data: any) => {
        handleSocketNotification({ event: SOCKET_EVENTS.NEW, data });
      };

      const handleOrderUpdate = (data: any) => {
        handleSocketNotification({ event: SOCKET_EVENTS.UPDATED, data });
      };

      const handleStatusUpdate = (data: any) => {
        handleSocketNotification({ event: SOCKET_EVENTS.STATUS_UPDATE, data });
      };

      socket.on(SOCKET_EVENTS.NEW, handleNewOrder);
      socket.on(SOCKET_EVENTS.UPDATED, handleOrderUpdate);
      socket.on(SOCKET_EVENTS.STATUS_UPDATE, handleStatusUpdate);

      // Cleanup subscriptions on unmount
      return () => {
        socket.off(SOCKET_EVENTS.NEW, handleNewOrder);
        socket.off(SOCKET_EVENTS.UPDATED, handleOrderUpdate);
        socket.off(SOCKET_EVENTS.STATUS_UPDATE, handleStatusUpdate);
        vendorIds.forEach(vendorId => {
          socket.unsubscribeFromVendor(vendorId);
        });
      };
    }
  }, [socket, orders, subscribedVendors, handleSocketNotification]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      'PENDING': theme.warning || '#f59e0b',
      'ACCEPTED': theme.components?.status?.accepted?.text || '#5B9BD5',
      'PREPARING': theme.components?.status?.preparing?.text || '#5B9BD5',
      'READY': theme.success || '#10b981',
      'COMPLETED': theme.success || '#10b981',
      'CANCELLED': theme.danger || '#ef4444',
      'REJECTED': theme.danger || '#ef4444'
    };
    return colors[status] || theme.gray?.[500] || '#6b7280';
  };

  const handleSearch = () => {
    setPage(1);
    fetchOrders();
  };

  return (
    <div className="p-6" style={{ backgroundColor: theme.background }}>
      {/* Toaster provided globally via ToastProvider */}

      {/* Header */}
      <div className="mb-6">
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 1 }}>
          <div>
            <h1
              className="text-2xl font-bold mb-2"
              style={{ color: theme.text, wordBreak: 'keep-all', overflowWrap: 'normal' }}
            >
              Admin Orders Management
            </h1>
            <p style={{ color: theme.gray?.[600] }}>
              Monitor and manage all orders across the platform
            </p>
          </div>
          <Chip
            label={connected ? 'Real-time Connected' : 'Real-time Disconnected'}
            color={connected ? 'success' : 'warning'}
            variant="outlined"
            size="small"
            icon={connected ? <TrendingUpIcon /> : undefined}
            sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
          />
        </Box>
      </div>

      {/* Stats Cards */}
      <Grid container spacing={{ xs: 1, sm: 2 }} className="mb-4">
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%)', border: 'none' }}>
            <CardContent sx={{ p: { xs: 1.25, sm: 2 }, '&:last-child': { pb: { xs: 1.25, sm: 2 } } }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <div>
                  <Typography sx={{ color: 'var(--text-inverse)', fontWeight: 700, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {stats.totalOrders}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.25, fontWeight: 500, fontSize: { xs: '0.65rem', sm: '0.875rem' } }}>
                    Total Orders
                  </Typography>
                </div>
                <Box sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' } }}><ReceiptIcon style={{ color: 'var(--text-inverse)', fontSize: 36 }} /></Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #34d399 0%, #059669 100%)', border: 'none' }}>
            <CardContent sx={{ p: { xs: 1.25, sm: 2 }, '&:last-child': { pb: { xs: 1.25, sm: 2 } } }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <div style={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ color: 'var(--text-inverse)', fontWeight: 700, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {CURRENCY.symbol}{stats.totalRevenue.toFixed(2)}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.25, fontWeight: 500, fontSize: { xs: '0.65rem', sm: '0.875rem' } }}>
                    Revenue
                  </Typography>
                </div>
                <Box sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' } }}><AttachMoneyIcon style={{ color: 'var(--text-inverse)', fontSize: 36 }} /></Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)', border: 'none' }}>
            <CardContent sx={{ p: { xs: 1.25, sm: 2 }, '&:last-child': { pb: { xs: 1.25, sm: 2 } } }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <div>
                  <Typography sx={{ color: 'var(--text-inverse)', fontWeight: 700, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {stats.activeOrders}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.25, fontWeight: 500, fontSize: { xs: '0.65rem', sm: '0.875rem' } }}>
                    Active Orders
                  </Typography>
                </div>
                <Box sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' } }}><TrendingUpIcon style={{ color: 'var(--text-inverse)', fontSize: 36 }} /></Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)', border: 'none' }}>
            <CardContent sx={{ p: { xs: 1.25, sm: 2 }, '&:last-child': { pb: { xs: 1.25, sm: 2 } } }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <div>
                  <Typography sx={{ color: 'var(--text-inverse)', fontWeight: 700, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {stats.totalVendors}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.25, fontWeight: 500, fontSize: { xs: '0.65rem', sm: '0.875rem' } }}>
                    Vendors
                  </Typography>
                </div>
                <Box sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' } }}><PeopleIcon style={{ color: 'var(--text-inverse)', fontSize: 36 }} /></Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card className="mb-4" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by order number, customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: <SearchIcon className="mr-2" style={{ color: theme.gray?.[500] }} />
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="ALL">All Status</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="ACCEPTED">Accepted</MenuItem>
                  <MenuItem value="PREPARING">Preparing</MenuItem>
                  <MenuItem value="READY">Ready</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <DateFilterDropdown
                value={dateFilter}
                onChange={setDateFilter}
                showAllOption={true}
              />
            </Grid>

          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <TableContainer component={Paper} sx={{
        width: '100%',
        overflowX: 'auto',
        '& .MuiTable-root': {
          minWidth: { xs: '800px', md: '1000px', lg: '1200px' }
        }
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                minWidth: '100px',
                whiteSpace: 'nowrap'
              }}>Order #</TableCell>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                minWidth: '140px',
                whiteSpace: 'nowrap'
              }}>Customer</TableCell>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                minWidth: '120px',
                whiteSpace: 'nowrap'
              }}>Vendor</TableCell>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                minWidth: '80px',
                whiteSpace: 'nowrap'
              }}>Items</TableCell>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                minWidth: '100px',
                whiteSpace: 'nowrap'
              }}>Total</TableCell>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                minWidth: '100px',
                whiteSpace: 'nowrap'
              }}>Status</TableCell>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                minWidth: '120px',
                whiteSpace: 'nowrap'
              }}>Date</TableCell>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                minWidth: '80px',
                whiteSpace: 'nowrap'
              }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <div className="py-8">Loading orders...</div>
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <div className="py-8">No orders found</div>
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell sx={{ minWidth: '100px' }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        whiteSpace: 'nowrap'
                      }}
                    >
                      #{order.orderNumber || order.id}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: '140px' }}>
                    <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {order.user?.username || 'Guest Customer'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          color: theme.gray?.[600],
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {order.user?.email || 'N/A'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ minWidth: '120px' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {order.vendor?.username || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: '80px' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        textAlign: 'center'
                      }}
                    >
                      {order.items.length} items
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: '100px' }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {CURRENCY.symbol}{calculateOrderTotal(order).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: '100px' }}>
                    <Chip
                      label={order.status}
                      size="small"
                      sx={{
                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                        height: { xs: 20, sm: 24 },
                        backgroundColor: getStatusColor(order.status) + '20',
                        color: getStatusColor(order.status)
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: '120px' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {format(new Date(order.createdAt), 'MMM dd, HH:mm')}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: '80px' }}>
                    <AppIconButton
                      icon={<VisibilityIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                      tooltip="View Details"
                      variant="edit"
                      size="small"
                      onClick={() => {
                        // For now, show order details in a toast
                        const details = order.items.map(item =>
                          `${item.quantity}x ${item.menuItemName} (${CURRENCY.symbol}${item.price})`
                        ).join(', ');
                        toast(details, { duration: 5000, icon: '📋' });
                      }}
                      sx={{
                        width: 36,
                        height: 36,
                        border: '1px solid rgba(96, 165, 250, 0.31)',
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-4">
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </div>
      )}
    </div>
  );
};

export default AdminOrders;